import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { TbBuildingArch, TbServer2, TbBrain, TbRocket } from "react-icons/tb";
import { colors, font, pagePx } from "../theme/tokens";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

const pillars = [
  {
    icon: <TbBuildingArch size={26} />,
    title: "Software Architecture",
    text: "Microservices, event-driven systems (Kafka/CQRS), and design patterns applied to real production constraints — not just theory.",
  },
  {
    icon: <TbServer2 size={26} />,
    title: "Backend Engineering",
    text: "NestJS & FastAPI services, PostgreSQL, Redis, message queues, and clean API design with observability baked in.",
  },
  {
    icon: <TbBrain size={26} />,
    title: "AI-Native Products",
    text: "LLM agents, generative pipelines, and virtual try-on — shipping AI features that solve concrete business problems.",
  },
  {
    icon: <TbRocket size={26} />,
    title: "End-to-End Delivery",
    text: "From architecture and DevOps (Docker, CI/CD) to React front-ends — I take products from whiteboard to production.",
  },
];

const About = () => {
  return (
    <Box component="section" id="about" sx={{ px: pagePx, py: { xs: 8, md: 12 }, position: "relative", backgroundColor: colors.bgAlt }}>
      <SectionHeading
        eyebrow="About"
        title="An engineer who ships — and cares how it's built"
      />

      <Reveal>
        <Box sx={{ maxWidth: 900, mx: "auto", textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography sx={{ fontSize: { xs: 16, md: 17.5 }, color: colors.inkSoft, lineHeight: 1.85, mb: 2.5 }}>
            I'm <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>Ibrahim Hamdani</Box>, a
            final-year software engineering student at the{" "}
            <Box component="a" href="https://www.esi-sba.dz/fr/" target="_blank" rel="noopener noreferrer"
              sx={{ color: colors.accent, fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              École Supérieure en Informatique (ESI)
            </Box>
            , graduating in 2026 — but I've spent the last few years shipping real, revenue-generating
            software, not just coursework.
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 17.5 }, color: colors.inkSoft, lineHeight: 1.85 }}>
            My focus is <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>software architecture</Box> and
            <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}> distributed systems</Box>. I've designed an
            8-service, event-driven virtual try-on platform, built SaaS products used by real businesses, and I'm
            equally comfortable reasoning about a Kafka topology or polishing a React component. I'm looking for a
            team where I can grow as an engineer and own meaningful architecture.
          </Typography>
        </Box>
      </Reveal>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ flexWrap: "wrap", justifyContent: "center" }}>
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 90} sx={{ flex: { md: "1 1 220px" }, maxWidth: { md: 300 } }}>
            <Box
              sx={{
                height: "100%",
                p: 3,
                borderRadius: "16px",
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                transition: "all 0.25s ease",
                "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(99,102,241,0.5)", boxShadow: "0 16px 40px rgba(0,0,0,0.35)" },
              }}
            >
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: "13px", mb: 2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(99,102,241,0.12)", color: colors.primary,
                  border: "1px solid rgba(99,102,241,0.25)",
                }}
              >
                {p.icon}
              </Box>
              <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 17, color: colors.ink, mb: 1 }}>
                {p.title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: colors.inkSoft, lineHeight: 1.65 }}>{p.text}</Typography>
            </Box>
          </Reveal>
        ))}
      </Stack>
    </Box>
  );
};

export default About;
